import throttle from "lodash-es/throttle";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { fetchAuthToken } from "app/Auth";
import { client } from "common/apiClient";
import { BuilderMap } from "features/builder/types";

export const getBuilderMaps = createAsyncThunk<
  BuilderMap[],
  {
    uid: string;
  }
>("builder/getBuilderMaps", async ({ uid }) => {
  const token = await fetchAuthToken();
  const response = await client({
    token: token,
  }).get(`/v1/builder/map?uid=${uid}`);
  return response.data;
});

export const createBuilderMap = createAsyncThunk<
  BuilderMap,
  {
    uid: string;
    name: string;
  }
>("builder/createBuilderMap", async ({ uid, name }) => {
  const token = await fetchAuthToken();
  const response = await client({
    token: token,
  }).post(`/v1/builder/map?uid=${uid}`, {
    name,
    content: { size: { x: 4, y: 4, z: 4 }, map: [] },
  });
  return response.data;
});

export const getBuilderMapContent = createAsyncThunk(
  "builder/getBuilderMapContent",
  async ({ builder_map_id }: { builder_map_id: string }) => {
    const token = await fetchAuthToken();
    const response = await client({
      token: token,
    }).get(`/v1/builder/map/content?builder_map_id=${builder_map_id}`);
    return response.data;
  },
);

export const updateBuilderMap = createAsyncThunk(
  "builder/updateBuilderMap",
  async ({
    uid,
    builder_map_id,
    map,
  }: {
    uid: string;
    builder_map_id: string;
    map: Partial<BuilderMap>;
  }) => {
    const token = await fetchAuthToken();
    const response = await client({
      token: token,
    }).put(`/v1/builder/map?uid=${uid}&builder_map_id=${builder_map_id}`, { ...map });
    return response.data;
  },
);

export const updateBuilderMapContent = createAsyncThunk(
  "builder/updateBuilderMapContent",
  throttle(async ({ uid }: { uid: string }, thunkAPI) => {
    const token = await fetchAuthToken();
    const { builder } = thunkAPI.getState() as RootState;
    try {
      await client({
        token: token,
      }).put(`/v1/builder/map/content?uid=${uid}&builder_map_id=${builder.editingBuilderMapId}`, {
        content: {
          size: builder.size,
          map: builder.map.entities,
        },
      });
    } catch (error) {
      console.error(error);
    }
  }, 5000),
);

export const deleteBuilderMap = createAsyncThunk(
  "builder/deleteBuilderMap",
  async ({ uid, builder_map_id }: { uid: string; builder_map_id: string }) => {
    const token = await fetchAuthToken();
    await client({
      token: token,
    }).delete(`/v1/builder/map/completely?uid=${uid}&builder_map_id=${builder_map_id}`);
    return builder_map_id;
  },
);

// AI マップ生成
export interface GeneratedMapData {
  tiles: Array<{ x: number; z: number; name: string }>;
  character: { x: number; z: number };
  goal: { x: number; z: number };
  gems: Array<{ x: number; z: number }>;
  decorations: Array<{ x: number; z: number; name: string }>;
}

interface GenerateMapRequest {
  prompt?: string;
  difficulty: "easy" | "medium" | "hard";
  size: { x: number; z: number };
}

export const generateMap = async (request: GenerateMapRequest): Promise<GeneratedMapData> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).post<GeneratedMapData>(
    "/v1/builder/map/generate",
    request,
  );
  return response.data;
};
